import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import config from '../../../../../config';
import { history } from '../../../../../helpers/history';
import Notify from '../../../../../shared/components/widgets/Notify';
import { renderTextArea } from '../../../shared/formComponents';

const DEBOUNCE_MS = 250;
const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 50;
const MAX_QUERY_LENGTH = 200;
const DEFAULT_TIMEOUT_MS = 15000;
// Google and Meta may block on the first request for an account while the catalog syncs.
const SYNC_TIMEOUT_MS = 60000;
const SYNC_PLATFORMS = ['google', 'meta'];

const isSyncPlatform = platform => SYNC_PLATFORMS.includes(platform);

const buildHeaders = jwtToken => ({ ...config.jsonHeader, authorization: `JWT ${jwtToken}` });

/**
 * Normalizes a platform-specific autocomplete item to { id, primary, secondary }.
 * Amazon returns `sku` (SKU accounts) or `asin` (ASIN accounts), so show whichever is present.
 */
export function normalizeItem(platform, item) {
  if (!item) return null;
  if (platform === 'amazon') {
    const id = item.sku || item.asin;
    return id ? { id: String(id), primary: String(id), secondary: item.title || '' } : null;
  }
  if (platform === 'google') {
    return item.id ? { id: String(item.id), primary: String(item.id), secondary: item.title || '' } : null;
  }
  if (platform === 'meta') {
    if (!item.productSetId) return null;
    const count = item.productCount;
    let countLabel = '';
    if (count !== null && count !== undefined) {
      countLabel = `(${count} ${Number(count) === 1 ? 'product' : 'products'})`;
    }
    return {
      id: String(item.productSetId),
      primary: String(item.productSetId),
      secondary: [item.name || '', countLabel].filter(Boolean).join(' '),
    };
  }
  return null;
}

/**
 * Triggers a backend catalog cache refresh (Google and Meta only) and shows a notification.
 * The refresh runs in the background, so the user is told to check back later.
 */
export async function refreshCatalog({ platform, accountId, jwtToken }) {
  try {
    const response = await fetch(`${config.serverUrl}/catalog/autocomplete/${platform}/refresh`, {
      method: 'POST',
      headers: buildHeaders(jwtToken),
      body: JSON.stringify({ accountId: Number(accountId) }),
    });
    if (response.status === 401) {
      history.push('/logout');
      return false;
    }
    if (response.status === 403) throw new Error('You do not have access to this catalog');
    if (!response.ok) throw new Error(response.statusText || `Request failed (${response.status})`);
    Notify.show(
      'primary',
      'Catalog refresh running',
      'The catalog refresh is running in the background. Check back later and refresh the page to see changes.',
    );
    return true;
  } catch (error) {
    Notify.show('danger', 'Error', `Failed to start catalog refresh: ${error.message || error}`);
    return false;
  }
}

const panelStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 20,
  margin: 0,
  padding: '4px 0',
  listStyle: 'none',
  backgroundColor: 'var(--dropdown-bg, #fff)',
  border: '1px solid rgba(0, 0, 0, 0.15)',
  borderRadius: '4px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  maxHeight: '260px',
  overflowY: 'auto',
};

const messageStyle = { padding: '6px 10px', color: '#999', fontSize: '0.9em' };

/**
 * CatalogAutocomplete
 *
 * Reusable autocomplete for picking a catalog item for Amazon, Google or Meta via the
 * anine-server gateway (GET /catalog/autocomplete/:platform). Debounced, cancels stale
 * requests, and handles loading / empty / error / no-access states.
 *
 * @param {'amazon'|'google'|'meta'} platform
 * @param {string|number} accountId - numeric account id
 * @param {string} value - currently selected identifier
 * @param {Function} onChange - called with the identifier (sku/asin, id or productSetId)
 * @param {string} [placeholder]
 * @param {boolean} [disabled]
 * @param {boolean} [clearOnSelect] - clear the input after a pick (used by list fields)
 */
const CatalogAutocomplete = ({
  platform, accountId, value, onChange, placeholder, disabled, clearOnSelect,
}) => {
  const user = useSelector(store => store.authentication.user);
  const [text, setText] = useState(value || '');
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState(null); // { status, message }
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const lastQueryRef = useRef('');

  const numericAccountId = /^\d+$/.test(String(accountId)) ? String(accountId) : null;

  // Keep the visible text in sync when the value changes from outside.
  useEffect(() => {
    setText(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cancel any pending debounce / request on unmount.
  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const runSearch = async (rawQuery) => {
    const query = (rawQuery || '').slice(0, MAX_QUERY_LENGTH);
    lastQueryRef.current = query;
    if (!numericAccountId) {
      setError({ status: 400, message: 'A valid account is required' });
      setHasSearched(true);
      return;
    }

    // Cancel the stale request.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, isSyncPlatform(platform) ? SYNC_TIMEOUT_MS : DEFAULT_TIMEOUT_MS);

    setIsLoading(true);
    setError(null);
    try {
      const limit = Math.min(DEFAULT_LIMIT, MAX_LIMIT);
      const url = `${config.serverUrl}/catalog/autocomplete/${platform}?accountId=${numericAccountId}&q=${encodeURIComponent(query)}&limit=${limit}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders(user.jwtToken),
        signal: controller.signal,
      });
      if (response.status === 401) {
        history.push('/logout');
        return;
      }
      if (response.status === 403) {
        setItems([]);
        setError({ status: 403, message: 'No access' });
        setHasSearched(true);
        return;
      }
      if (!response.ok) {
        let message = response.statusText || `Request failed (${response.status})`;
        try {
          const body = await response.json();
          message = body.message || body.error || message;
        } catch (e) {
          // Non-JSON error body; keep the status text.
        }
        throw Object.assign(new Error(message), { status: response.status });
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : (data.results || data.items || data.data || []);
      setItems(list.map(item => normalizeItem(platform, item)).filter(Boolean));
      setHighlightedIndex(-1);
      setHasSearched(true);
    } catch (e) {
      if (controller.signal.aborted && !timedOut) return; // superseded by a newer request
      setItems([]);
      setError({
        status: e.status || 0,
        message: timedOut ? 'The request timed out' : (e.message || 'Something went wrong'),
      });
      setHasSearched(true);
    } finally {
      clearTimeout(timeoutId);
      if (abortRef.current === controller) {
        setIsLoading(false);
      }
    }
  };

  const scheduleSearch = (query, immediate = false) => {
    clearTimeout(debounceRef.current);
    if (immediate) {
      runSearch(query);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query), DEBOUNCE_MS);
  };

  const handleInputChange = (event) => {
    const next = event.target.value;
    setText(next);
    setIsOpen(true);
    if (!clearOnSelect) onChange(next);
    // Empty input loads the first page (q=); otherwise search on 1+ characters.
    scheduleSearch(next);
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    // Focused and empty: load the first page (also triggers the backend cache refresh).
    if (!text) scheduleSearch('', true);
  };

  const handleSelect = (item) => {
    onChange(item.id);
    setText(clearOnSelect ? '' : item.id);
    setIsOpen(false);
    setItems([]);
    setHasSearched(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(index => Math.min(index + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && isOpen && highlightedIndex >= 0 && items[highlightedIndex]) {
      event.preventDefault();
      handleSelect(items[highlightedIndex]);
    }
  };

  const showPanel = isOpen && (isLoading || items.length > 0 || hasSearched || error);

  return (
    <div style={{ width: '100%' }}>
      <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          style={{ width: '100%' }}
        />
        {showPanel && (
          <ul style={panelStyle}>
            {isLoading && (
              <li style={messageStyle}>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                {' '}
                {isSyncPlatform(platform) ? 'Loading... syncing the catalog may take a moment' : 'Loading...'}
              </li>
            )}
            {!isLoading && error && error.status === 403 && (
              <li style={messageStyle}>You don&apos;t have access to this catalog</li>
            )}
            {!isLoading && error && error.status !== 403 && (
              <li style={{ ...messageStyle, color: '#ff4861' }}>
                {error.message}
                {' '}
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => scheduleSearch(lastQueryRef.current, true)}
                >
                  Retry
                </button>
              </li>
            )}
            {!isLoading && !error && hasSearched && items.length === 0 && (
              <li style={messageStyle}>No matches</li>
            )}
            {!error && items.map((item, index) => (
              <li key={item.id}>
                <button
                  type="button"
                  onMouseDown={event => event.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(item)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: index === highlightedIndex ? 'rgba(145, 190, 35, 0.15)' : 'transparent',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    font: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{item.primary}</span>
                  {item.secondary && (
                    <span style={{ opacity: 0.7, fontSize: '0.85em' }}>{` - ${item.secondary}`}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

CatalogAutocomplete.propTypes = {
  platform: PropTypes.oneOf(['amazon', 'google', 'meta']).isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  clearOnSelect: PropTypes.bool,
};

CatalogAutocomplete.defaultProps = {
  accountId: null,
  value: '',
  placeholder: 'Search catalog...',
  disabled: false,
  clearOnSelect: false,
};

/**
 * redux-form adapter for a single identifier value (e.g. Meta Product Set ID).
 */
export const CatalogAutocompleteField = ({
  input, platform, accountId, placeholder, disabled,
}) => (
  <CatalogAutocomplete
    platform={platform}
    accountId={accountId}
    value={input.value || ''}
    onChange={input.onChange}
    placeholder={placeholder}
    disabled={disabled}
  />
);

CatalogAutocompleteField.propTypes = {
  input: PropTypes.shape().isRequired,
  platform: PropTypes.oneOf(['amazon', 'google', 'meta']).isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

CatalogAutocompleteField.defaultProps = {
  accountId: null,
  placeholder: undefined,
  disabled: false,
};

/**
 * redux-form adapter for comma-separated identifier lists. Renders the picker above the
 * existing textarea; picking a row appends its identifier (case-insensitively de-duplicated).
 */
export const CatalogAutocompleteListField = ({
  input, meta, platform, accountId, placeholder,
}) => {
  const handlePick = (id) => {
    if (!id) return;
    const current = (input.value || '').split(',').map(item => item.trim()).filter(Boolean);
    if (current.some(item => item.toUpperCase() === id.toUpperCase())) return;
    input.onChange([...current, id].join(','));
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '6px' }}>
        <CatalogAutocomplete
          platform={platform}
          accountId={accountId}
          value=""
          onChange={handlePick}
          placeholder={placeholder}
          clearOnSelect
        />
      </div>
      {renderTextArea({ input, meta: meta || {} })}
    </div>
  );
};

CatalogAutocompleteListField.propTypes = {
  input: PropTypes.shape().isRequired,
  meta: PropTypes.shape(),
  platform: PropTypes.oneOf(['amazon', 'google', 'meta']).isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
};

CatalogAutocompleteListField.defaultProps = {
  meta: null,
  accountId: null,
  placeholder: 'Search catalog to add an item...',
};

export default CatalogAutocomplete;
